import { useEffect, useMemo, useState } from 'react';

import { EBadgePosition, EBundlesViewModel } from '@appcharge/shared-ui';
import useBadges from 'api/useBadges';
import { Badge, BundleBadge, EBadgeType } from 'common/contracts';

import { createBadgeFilter } from '../Offer.utils';

interface IUseBadgeFormProps {
  publisherId: string | undefined;
  relevantBadgeType: EBadgeType | 'both';
  viewModel: EBundlesViewModel | undefined;
  initialSelectedBadgeId: string | null | undefined;
  initialBadges?: Array<{ badge: Badge }>;
}

interface IUseBadgeFormReturn {
  badges: Array<{ badge: Badge }>;
  emblemBadges: object[];
  ribbonBadges: object[];
  badgeList: BundleBadge[];
  filterRelevantBadges: (badgeType: EBadgeType) => boolean;
  getBadges: ReturnType<typeof useBadges>['getBadges'];
  setSelectedBadgeId: (id: string | null) => void;
  selectedPublisherBadgeId: string;
}

/**
 * Manages badge state, filtering, and sync effects shared between
 * Bundles and Promotions forms.
 *
 * @example
 * const { badges, setSelectedBadgeId, selectedPublisherBadgeId } = useBadgeForm({
 *   publisherId,
 *   relevantBadgeType,
 *   viewModel,
 *   initialSelectedBadgeId: initialBadgeValues.badgeId
 * });
 */
export const useBadgeForm = ({
  publisherId,
  relevantBadgeType,
  viewModel,
  initialSelectedBadgeId,
  initialBadges
}: IUseBadgeFormProps): IUseBadgeFormReturn => {
  const { getBadges } = useBadges(publisherId);
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(
    initialSelectedBadgeId ?? null
  );
  const [badges, setBadges] = useState<Array<{ badge: Badge }>>(
    initialBadges || []
  );
  const [emblemBadges, setEmblemBadges] = useState<object[]>([]);
  const [ribbonBadges, setRibbonBadges] = useState<object[]>([]);
  const [badgeList, setBadgeList] = useState<BundleBadge[]>([]);

  const filterRelevantBadges = useMemo(
    () => createBadgeFilter(relevantBadgeType, viewModel),
    [relevantBadgeType, viewModel]
  );

  const selectedPublisherBadgeId =
    badges[0]?.badge?.publisherBadgeId || '';

  useEffect(() => {
    if (selectedBadgeId) {
      const currentBadge = getBadges?.data?.result?.find(
        (b: Badge): boolean => b.badgeId === selectedBadgeId
      );
      if (currentBadge) {
        setBadges([{ badge: currentBadge }]);
        if (currentBadge?.emblem) {
          setEmblemBadges([currentBadge.emblem]);
        } else {
          setEmblemBadges([]);
        }
        if (currentBadge?.ribbon) {
          setRibbonBadges([currentBadge.ribbon]);
        } else {
          setRibbonBadges([]);
        }
      }
    } else {
      setBadges([]);
      setEmblemBadges([]);
      setRibbonBadges([]);
    }
  }, [selectedBadgeId, getBadges?.data?.result]);

  useEffect(() => {
    if (!getBadges.data?.result) {
      return;
    }
    const badgesDropdownItems: BundleBadge[] = getBadges.data.result
      .map((badge: Badge): BundleBadge | undefined => {
        if (filterRelevantBadges(badge.type)) {
          return {
            content: badge.name,
            name: badge.name,
            key: badge.badgeId || '',
            value: badge.badgeId || '',
            url: badge.emblem?.imageUrl,
            position: badge.position as EBadgePosition
          };
        }
        return undefined;
      })
      .filter(
        (b: BundleBadge | undefined): b is BundleBadge => b !== undefined
      );
    setBadgeList(badgesDropdownItems);
  }, [getBadges.data, filterRelevantBadges]);

  return {
    badges,
    emblemBadges,
    ribbonBadges,
    badgeList,
    filterRelevantBadges,
    getBadges,
    setSelectedBadgeId,
    selectedPublisherBadgeId
  };
};
